import { useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { t } from '../services/language-manager';

/** How long the Save button stays on "Saved" before it returns to Save. */
export const SAVED_CONFIRMATION_MS = 2000;
// Success text beside the button; its CSS fade ends at the same time. Errors never auto-hide.
export const SAVE_MESSAGE_MS = 4000;

/** `id` changes on every save, so the same text shown again restarts its fade. */
export interface SaveMessage {
  text: string;
  id: number;
}

let saveMessageNonce = 0;

export function nextSaveMessage(text: string): SaveMessage {
  saveMessageNonce += 1;
  return { text, id: saveMessageNonce };
}

/**
 * One form's Save confirmation: the green "Saved" button for
 * SAVED_CONFIRMATION_MS and the success text for SAVE_MESSAGE_MS.
 * Errors stay with the form, which shows them in the same SaveRow.
 */
export function useSaveConfirmation() {
  const [justSaved, setJustSaved] = useState(false);
  const [message, setMessage] = useState<SaveMessage | null>(null);
  const savedTimer = useRef<number | null>(null);
  const messageTimer = useRef<number | null>(null);

  const stopTimers = () => {
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    if (messageTimer.current) window.clearTimeout(messageTimer.current);
    savedTimer.current = null;
    messageTimer.current = null;
  };

  useEffect(() => stopTimers, []);

  const clear = useCallback(() => {
    stopTimers();
    setJustSaved(false);
    setMessage(null);
  }, []);

  /** `flash: false` when another button saved (the text only, Save stays as it is). */
  const confirm = useCallback((text: string, { flash = true }: { flash?: boolean } = {}) => {
    stopTimers();
    const next = nextSaveMessage(text);
    setJustSaved(flash);
    setMessage(next);
    if (flash) {
      savedTimer.current = window.setTimeout(() => {
        savedTimer.current = null;
        setJustSaved(false);
      }, SAVED_CONFIRMATION_MS);
    }
    messageTimer.current = window.setTimeout(() => {
      messageTimer.current = null;
      setMessage((current) => (current?.id === next.id ? null : current));
    }, SAVE_MESSAGE_MS);
  }, []);

  return { justSaved, message, confirm, clear };
}

interface SaveRowProps {
  /** Error text; shown instead of the success message, and never auto-hidden. */
  error?: string | null;
  message?: SaveMessage | null;
  /** On the feedback area, for controls that are described by it. */
  feedbackId?: string;
  /** Spacing for where the row sits, for example `settings-section__save`. */
  className?: string;
  /** The row's buttons; Save last, so it sits at the end. */
  children?: ReactNode;
}

/**
 * Save row: the result of a click on the start side, the buttons on the end
 * side, so the message is where the click was. Mirrors under dir="rtl".
 */
export function SaveRow({ error, message, feedbackId, className, children }: SaveRowProps) {
  return (
    <div className={`save-row${className ? ` ${className}` : ''}`}>
      <div className="save-row__feedback" id={feedbackId}>
        {error ? (
          <p className="save-row__message save-row__message--error" role="alert">
            {error}
          </p>
        ) : message ? (
          <p key={message.id} className="save-row__message save-row__message--success" role="status">
            {message.text}
          </p>
        ) : null}
      </div>
      {children && <div className="save-row__buttons">{children}</div>}
    </div>
  );
}

interface SaveButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'type'> {
  saving: boolean;
  justSaved: boolean;
  label: string;
  /** Defaults to "Saving…". */
  savingLabel?: string;
  /** Defaults to "Saved". */
  savedLabel?: string;
  variant?: 'primary' | 'secondary';
}

/**
 * Save button: spinner while saving, then green with a check mark for
 * SAVED_CONFIRMATION_MS. Disabled while saving and while it shows Saved.
 */
export function SaveButton({
  saving,
  justSaved,
  label,
  savingLabel,
  savedLabel,
  variant = 'primary',
  className,
  disabled,
  ...rest
}: SaveButtonProps) {
  let content: ReactNode = label;
  if (saving) {
    content = (
      <>
        <span className="btn-spinner" aria-hidden="true" />
        {savingLabel ?? t('settings-actions-saving')}
      </>
    );
  } else if (justSaved) {
    content = (
      <>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 6L9 17l-5-5" />
        </svg>
        {savedLabel ?? t('settings-actions-saved')}
      </>
    );
  }
  return (
    <button
      type="button"
      className={`btn btn--${variant} save-btn${justSaved ? ' save-btn--saved' : ''}${className ? ` ${className}` : ''}`}
      disabled={disabled || saving || justSaved}
      aria-busy={saving}
      {...rest}
    >
      {content}
    </button>
  );
}
